// ─────────────────────────────────────────────────────────────────────────────
// 📘 PLAN MARKETING MIHI : règles de calcul de la rémunération d'équipe
// (source : document « Plan de marketing » transmis par Melissa)
// Fichier de calcul pur, sans affichage : testable et réutilisable.
//
// Vocabulaire du plan
//  PV  = volume personnel (mes commandes sur la période de 21 jours), minimum 25 €
//  PGV = volume du groupe personnel = mon PV + le volume de toutes les personnes de mon groupe
//        (les « groupes de directeurs » en sont exclus : ils donnent droit à des primes à part)
//  Ma rémunération = différence entre MON pourcentage et celui de chaque personne de mon groupe
// ─────────────────────────────────────────────────────────────────────────────

export const PV_MIN = 25;
export const SEUIL_DIRECTEUR = 5000;

// Tableau 1 : niveau selon le PGV (avec PV ≥ 25 €)
export const NIVEAUX = [
  { id: "client",              nom: "Client",                 pgv: 0,    pct: 0 },
  { id: "consultant",          nom: "Consultante",            pgv: 100,  pct: 2 },
  { id: "conseiller",          nom: "Conseillère",            pgv: 250,  pct: 4 },
  { id: "conseiller_principal",nom: "Conseillère principale", pgv: 500,  pct: 6 },
  { id: "responsable",         nom: "Responsable",            pgv: 1000, pct: 8 },
  { id: "cadre_sup",           nom: "Cadre supérieure",       pgv: 1500, pct: 10 },
  { id: "partenaire",          nom: "Partenaire",             pgv: 2000, pct: 12 },
  { id: "dir_adjoint",         nom: "Directrice adjointe",    pgv: 3000, pct: 14 },
  { id: "directeur",           nom: "Directrice",             pgv: 5000, pct: 17 },
];

// Tableaux 3 et 4 : statuts de directrice (nombre de groupes de directrices de 1re ligne),
// PGV minimum, pourcentages versés sur les lignes 1 à 5 et prime de statut (une fois)
export const STATUTS_DIR = [
  { id: "directeur",            nom: "Directrice",                              groupes: 0,  elite: 0,  pgv: 5000, lignes: [],                         bonus: 1000 },
  { id: "principal",            nom: "Directrice principale",                   groupes: 1,  elite: 0,  pgv: 2500, lignes: [5],                        bonus: 500 },
  { id: "structures",           nom: "Directrice des structures",               groupes: 2,  elite: 0,  pgv: 2000, lignes: [5, 1],                     bonus: 2000 },
  { id: "commercial",           nom: "Directrice commerciale",                  groupes: 3,  elite: 0,  pgv: 1500, lignes: [5, 1, 0.5],                bonus: 3000 },
  { id: "commercial_senior",    nom: "Directrice commerciale senior",           groupes: 4,  elite: 0,  pgv: 1500, lignes: [5, 1, 0.5, 0.25],          bonus: 4000 },
  { id: "elite",                nom: "Directrice d'élite",                      groupes: 6,  elite: 0,  pgv: 1500, lignes: [5, 1, 0.5, 0.25, 0.125],   bonus: 6000 },
  { id: "national",             nom: "Directrice nationale",                    groupes: 9,  elite: 0,  pgv: 1500, lignes: [5, 1.5, 0.75, 0.35, 0.125],bonus: 10000 },
  { id: "national_principal",   nom: "Directrice nationale principale",         groupes: 12, elite: 0,  pgv: 1500, lignes: [5, 1.5, 0.75, 0.35, 0.175],bonus: 15000 },
  { id: "international",        nom: "Directrice internationale",               groupes: 15, elite: 0,  pgv: 1500, lignes: [5, 1.5, 0.75, 0.35, 0.175],bonus: 20000 },
  { id: "international_senior", nom: "Directrice internationale senior",        groupes: 18, elite: 0,  pgv: 1500, lignes: [5, 2, 1, 0.5, 0.175],      bonus: 30000 },
  { id: "general",              nom: "Directrice générale",                     groupes: 22, elite: 0,  pgv: 1500, lignes: [5, 2, 1, 0.5, 0.175],      bonus: 50000 },
  { id: "general_principal",    nom: "Directrice générale principale",          groupes: 26, elite: 5,  pgv: 1500, lignes: [5, 2.5, 1.5, 0.75, 0.225], bonus: 100000 },
  { id: "general_30",           nom: "Directrice générale (30 groupes, 10 Élite)", groupes: 30, elite: 10, pgv: 1500, lignes: [5, 2.5, 1.5, 0.75, 0.225], bonus: 500000 },
];

// Remise client sur le prix catalogue selon le montant de la commande
export const remiseCommande = (montantCatalogue) => {
  const m = Number(montantCatalogue) || 0;
  if (m > 145) return 30;
  if (m > 70) return 25;
  return 20;
};

const nombre = (v) => {
  const n = parseFloat(String(v == null ? "" : v).replace(/\s/g, "").replace(",", "."));
  return isNaN(n) || n < 0 ? 0 : n;
};
const arrondi = (n) => Math.round(n * 100) / 100;

// Niveau du tableau 1. pvOk === false : PV < 25 € → pas de niveau (le volume remonte à la marraine)
export function niveauPour(pgv, pvOk = true) {
  if (pvOk === false) return NIVEAUX[0];
  let n = NIVEAUX[0];
  for (const x of NIVEAUX) if (nombre(pgv) >= x.pgv) n = x;
  return n;
}

export function prochainNiveau(pgv) {
  return NIVEAUX.find((x) => x.pgv > nombre(pgv)) || null;
}

// Statut de directrice selon le nombre de groupes de directrices de 1re ligne (et d'Élite)
export function statutDirectrice(nbGroupes, nbElite = 0) {
  let s = null;
  for (const x of STATUTS_DIR) {
    if (x.groupes <= nbGroupes && x.elite <= nbElite) s = x;
  }
  return s;
}

export function statutSuivant(nbGroupes, nbElite = 0) {
  const actuel = statutDirectrice(nbGroupes, nbElite);
  return STATUTS_DIR.find((x) => x.groupes > (actuel ? actuel.groupes : -1)) || null;
}

/**
 * Calcule la rémunération liée à l'équipe sur une période.
 *
 * @param {object} p
 * @param {number|string} p.pv            mon volume personnel (€)
 * @param {Array}  p.membres              [{ id, nom, pgv, pvOk (true/false/null=inconnu), ligne (1..5), bonus2 }]
 *                                        pgv = volume du groupe de cette personne (€)
 * @param {number} p.nbElite              nombre de groupes « Élite » (statuts 26 et 30)
 * @param {number} p.enfants              enfants nés/adoptés dans l'année (prime parentale)
 */
export function calculerEquipe({ pv = 0, membres = [], nbElite = 0, enfants = 0 } = {}) {
  const pvN = nombre(pv);
  const pvOk = pvN >= PV_MIN;

  const liste = (membres || []).map((m) => {
    const pgv = nombre(m.pgv);
    const pvMembreOk = m.pvOk === false ? false : true;
    const niveau = niveauPour(pgv, pvMembreOk);
    const estDirectrice = pgv >= SEUIL_DIRECTEUR && pvMembreOk;
    return { ...m, pgv, pvMembreOk, niveau, estDirectrice, ligne: Math.min(5, Math.max(1, parseInt(m.ligne, 10) || 1)) };
  });

  const groupe = liste.filter((m) => !m.estDirectrice);          // mon groupe personnel
  const directrices = liste.filter((m) => m.estDirectrice);      // groupes de directrices (exclus de mon PGV)

  const pgvMoi = arrondi(pvN + groupe.reduce((s, m) => s + m.pgv, 0));
  const nbGroupes1 = directrices.filter((m) => m.ligne === 1).length;
  const statut = nbGroupes1 > 0 ? statutDirectrice(nbGroupes1, nbElite) : null;
  const seuil = statut ? statut.pgv : SEUIL_DIRECTEUR;

  // Mon niveau
  let niveau = niveauPour(pgvMoi, pvOk);
  let panier = false;
  let estDirectrice = false;
  if (pvOk && nbGroupes1 > 0) {
    // Je dirige des groupes de directrices : 17 % dans tous les cas,
    // mais je ne suis directrice (et n'ai droit aux primes) que si mon PGV atteint le seuil du statut
    niveau = NIVEAUX[NIVEAUX.length - 1];
    estDirectrice = pgvMoi >= seuil;
    panier = !estDirectrice;
  } else if (pvOk && pgvMoi >= SEUIL_DIRECTEUR) {
    estDirectrice = true;
  }
  const pct = pvOk ? niveau.pct : 0;

  // 1. Rémunération sur le PGV : ma part = (mon % − le % de la personne) × son volume
  const lignes = groupe.map((m) => {
    const diff = Math.max(0, pct - m.niveau.pct);
    return { id: m.id, nom: m.nom, pgv: m.pgv, niveau: m.niveau, diff, gain: arrondi(m.pgv * diff / 100) };
  });
  const gainPV = pvOk ? arrondi(pvN * pct / 100) : 0;
  const gainGroupe = pvOk ? arrondi(lignes.reduce((s, l) => s + l.gain, 0)) : 0;

  // 2. Primes de directrice sur les groupes de directrices (tableau 3 + bonus 2e ligne)
  const primes = [];
  if (pvOk && estDirectrice && statut) {
    directrices.forEach((d) => {
      const base = statut.lignes[d.ligne - 1] || 0;
      const supplement = d.ligne === 2 && d.bonus2 ? 1 : 0;
      const taux = base + supplement;
      if (taux > 0) primes.push({ id: d.id, nom: d.nom, pgv: d.pgv, ligne: d.ligne, taux, gain: arrondi(d.pgv * taux / 100) });
    });
  }
  const gainPrimes = arrondi(primes.reduce((s, x) => s + x.gain, 0));

  // 3. Prime parentale : à partir de « Directrice adjointe » (14 %), 40 € le 1er enfant, 50 € les suivants, par période
  const nbEnfants = Math.max(0, parseInt(enfants, 10) || 0);
  const gainParental = pvOk && pct >= 14 && nbEnfants > 0 ? 40 + (nbEnfants - 1) * 50 : 0;

  const total = arrondi(gainPV + gainGroupe + gainPrimes + gainParental);

  // Pour motiver : combien il manque pour le niveau suivant
  const suivant = !estDirectrice && !panier ? prochainNiveau(pgvMoi) : null;

  // Prime de statut (versée une fois, après 6 périodes maintenues sur 12) : info
  let primeStatut = null;
  if (estDirectrice) {
    const st = statut || STATUTS_DIR[0];
    primeStatut = { nom: st.nom, montant: st.bonus };
  }

  return {
    pv: pvN, pvOk, pgv: pgvMoi, niveau, pct, panier, estDirectrice, statut, seuil, nbGroupes1,
    lignes, directrices, primes,
    gainPV, gainGroupe, gainPrimes, gainParental, total,
    suivant, manque: suivant ? arrondi(suivant.pgv - pgvMoi) : 0,
    primeStatut,
    prochainStatut: estDirectrice || nbGroupes1 > 0 ? statutSuivant(nbGroupes1, nbElite) : null,
  };
}
